import React, { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { FiEye, FiEyeOff } from "react-icons/fi";
import toast from "react-hot-toast";
import { API_URL } from "../../services/api.js";

const ShiprocketAuthModal = ({ userGoogleId, onClose, onSuccess }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            toast.error("Please enter both email and password.");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/auth/app/shiprocket/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${userGoogleId}`
                },
                body: JSON.stringify({ email, password }),
                credentials: "include"
            });

            const data = await response.json();

            if (!response.ok || data.error) {
                throw new Error(data.error || data.detail || "Authentication failed.");
            }

            toast.success("Shiprocket connected successfully!");
            onSuccess(); // Close modal and refresh list
        } catch (error) {
            toast.error(error.message || "Failed to connect to Shiprocket.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-2 border border-zinc-700 w-full max-w-md rounded-lg p-6 flex flex-col relative" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-zinc-200">Connect to Shiprocket</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
                        <IoMdClose size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm text-zinc-300 font-medium">Email Address</label>
                        <input
                            type="email"
                            placeholder="Enter your Shiprocket email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="bg-zinc-800 border border-zinc-600 rounded-md p-3 text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500 transition-colors"
                            required
                        />
                    </div>

                    <div className="flex flex-col gap-2 relative">
                        <label className="text-sm text-zinc-300 font-medium">Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter your Shiprocket password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="bg-zinc-800 border border-zinc-600 rounded-md p-3 w-full text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500 transition-colors pr-10"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                            >
                                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                            </button>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                            Generate API credentials from your Shiprocket account settings.<br/>
                            Go to Settings → API Users to create new credentials.
                        </p>
                    </div>

                    <div className="mt-4 flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="mr-3 px-4 py-2 text-zinc-300 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !email || !password}
                            className={`px-6 py-2 rounded-md font-medium text-white transition-colors ${loading ? "bg-blue-800 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                        >
                            {loading ? "Connecting..." : "Connect"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ShiprocketAuthModal;
